import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X, Mountain } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function Unsubscribe() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const category = params.get("category") || "";
    const [status, setStatus] = useState("pending"); // pending | done | error
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        if (!token || !category) {
            setStatus("error");
            setError("Missing token or category.");
            return;
        }
        api.post("/unsubscribe", { token, category })
            .then(({ data }) => {
                setEmail(data.email || "");
                setStatus("done");
            })
            .catch((err) => {
                setError(formatApiError(err));
                setStatus("error");
            });
    }, [token, category]);

    const label = {
        digest: "weekly digests",
        welcome: "welcome emails",
        marketing: "marketing emails",
    }[category] || "this category";

    return (
        <div className="min-h-screen flex items-center justify-center bg-alpine-mist p-4">
            <div
                data-testid="unsubscribe-card"
                className="max-w-md w-full bg-snowcap border border-ridge-line rounded-lg p-8 text-center"
            >
                <div className="flex items-center justify-center gap-2 mb-6 text-ember-orange">
                    <Mountain className="w-6 h-6" />
                    <span className="font-display font-black tracking-tight text-xl text-midnight-pine">
                        RealWorld Outdoors
                    </span>
                </div>

                {status === "pending" && (
                    <p className="text-slate-gray">Processing your request…</p>
                )}

                {status === "done" && (
                    <>
                        <div className="w-12 h-12 rounded-full bg-ember-orange/15 flex items-center justify-center mx-auto mb-4">
                            <Check className="w-6 h-6 text-ember-orange" />
                        </div>
                        <h1 className="font-display font-black text-2xl text-midnight-pine mb-2">
                            You're unsubscribed
                        </h1>
                        <p className="text-sm text-granite" data-testid="unsubscribe-confirmation">
                            {email ? <strong>{email}</strong> : "Your account"} will no longer receive {label}.
                            You can resubscribe anytime from your email preferences.
                        </p>
                        <div className="mt-6 flex items-center justify-center gap-3">
                            <Link
                                to="/settings/emails"
                                data-testid="unsubscribe-manage"
                                className="px-5 py-2 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark"
                            >
                                Manage preferences
                            </Link>
                            <Link
                                to="/feed"
                                className="px-5 py-2 rounded-md border border-midnight-pine text-midnight-pine font-bold text-sm hover:bg-midnight-pine hover:text-white"
                            >
                                Back to app
                            </Link>
                        </div>
                    </>
                )}

                {status === "error" && (
                    <>
                        <div className="w-12 h-12 rounded-full bg-slate-gray/20 flex items-center justify-center mx-auto mb-4">
                            <X className="w-6 h-6 text-slate-gray" />
                        </div>
                        <h1 className="font-display font-black text-2xl text-midnight-pine mb-2">
                            Couldn't process that link
                        </h1>
                        <p className="text-sm text-granite mb-4">{error}</p>
                        <Link
                            to="/settings/emails"
                            className="inline-block px-5 py-2 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark"
                        >
                            Manage preferences manually
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
